/**
 * Cloud Storage Connect Component
 * Connect/disconnect buttons for cloud providers (Google Drive, Dropbox)
 * Used in the settings page to manage cloud storage connections
 */

import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useLanguage } from '../../../hooks/useLanguage';
import { useCloudConnections } from '../hooks/useCloudConnections';
import { useGoogleAccess } from '@/modules/cloud-storage/hooks/useGoogleAccess';
import { forgetGoogleAccess } from '@/modules/cloud-storage/services/googleAccessService';
import { DriveAutoOnSetting } from '@/modules/cloud-storage/components/DriveAutoOnSetting';
import './CloudStorageConnect.css';

const PROVIDERS = [
  {
    key: 'google_drive',
    label: 'Google Drive',
    icon: (
      <svg viewBox="0 0 87.3 78" width="22" height="20">
        <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
        <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z" fill="#00ac47" />
        <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
        <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
        <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
        <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
      </svg>
    ),
  },
  {
    key: 'dropbox',
    label: 'Dropbox',
    icon: (
      <svg viewBox="0 0 43.35 40.3" width="22" height="20">
        <path fill="#0061FF" d="M12.87 0L0 8.14l8.8 7.07 12.87-8.14zM0 22.28l12.87 8.14 8.8-7.07L8.8 15.21zM21.67 23.35l8.8 7.07L43.35 22.28l-8.8-7.07zM43.35 8.14L30.47 0l-8.8 7.07 12.87 8.14zM21.72 24.77l-8.85 7.07-3.79-2.47v2.77l12.64 7.58 12.64-7.58v-2.77l-3.79 2.47z" />
      </svg>
    ),
  },
];

/** `refreshKey`: bumped by the page when a connection was just made in this
 *  tab (Settings finishes it after the page loaded), so the list reads again. */
export const CloudStorageConnect: React.FC<{ refreshKey?: number }> = ({ refreshKey = 0 }) => {
  const { t } = useLanguage();
  const { loading, loadConnections, connect, disconnect, isConnected, getConnection } =
    useCloudConnections();
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  // What the Google connection may do so far: Google's permissions are asked
  // one level at a time, when a feature first needs them (google_scopes).
  const googleAccess = useGoogleAccess(isConnected('google_drive'));
  const googleLevel = googleAccess?.read == null ? null
    : googleAccess.edit ? 'edit' : googleAccess.read ? 'read' : 'basic';

  useEffect(() => {
    loadConnections();
  }, [loadConnections, refreshKey]);

  const handleConnect = async (providerKey: string) => {
    try {
      await connect(providerKey);
    } catch (error: any) {
      console.error(`Error connecting ${providerKey}:`, error);
      message.error(error?.response?.data?.error_code === 'OAUTH_START_HOST'
        ? t('cloudStorage.wrongHost') : t('dataBundles.connectFailed', { provider: providerKey }));
    }
  };

  const handleDisconnect = async (providerKey: string) => {
    if (!window.confirm(t('dataBundles.confirmDisconnect', { provider: PROVIDERS.find((p) => p.key === providerKey)?.label }))) {
      return;
    }

    setDisconnecting(providerKey);
    try {
      await disconnect(providerKey);
      forgetGoogleAccess();     // also forgets 'is Drive connected?' (cached per page load)
      message.success(t('cloudStorage.disconnected'));
    } catch (error: any) {
      console.error(`Error disconnecting ${providerKey}:`, error);
      message.error(t('cloudStorage.disconnectFailed'));
    } finally {
      setDisconnecting(null);
    }
  };

  return (
    <div className="cloud-connect-container">
      <div className="cloud-connect-header">
        <h3 className="cloud-connect-title">{t('cloudStorage.title')}</h3>
        <p className="cloud-connect-description">
          {t('dataBundles.connectYourCloudStorageAccounts')}
        </p>
      </div>

      <div className="cloud-connect-providers">
        {PROVIDERS.map((provider) => {
          const connected = isConnected(provider.key);
          const connection = getConnection(provider.key);
          const isDisconnecting = disconnecting === provider.key;

          return (
            <div
              key={provider.key}
              className={`cloud-connect-provider ${connected ? 'connected' : ''}`}
            >
              <div className="cloud-connect-provider-icon">{provider.icon}</div>
              <div className="cloud-connect-provider-info">
                <span className="cloud-connect-provider-name" dir="auto">{provider.label}</span>
                {connected && connection ? (
                  <>
                    <span className="cloud-connect-provider-email" dir="auto">
                      {connection.account_email}
                    </span>
                    {provider.key === 'google_drive' && googleLevel && (
                      <span className="cloud-connect-provider-status">
                        {t(`cloudStorage.access.level.${googleLevel}`)}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="cloud-connect-provider-status">{t('cloudStorage.notConnected')}</span>
                )}
              </div>
              <div className="cloud-connect-provider-action">
                {connected ? (
                  <button
                    type="button"
                    className="cloud-connect-btn disconnect"
                    onClick={() => handleDisconnect(provider.key)}
                    disabled={isDisconnecting}
                  >
                    {isDisconnecting ? t('common.disconnecting') : t('common.disconnect')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="cloud-connect-btn connect"
                    onClick={() => handleConnect(provider.key)}
                    disabled={loading}
                  >
                    {t('cloudStorage.connect')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Each person turns Google Drive on per agent, in its conversation;
          this turns it on for every agent that allows it. */}
      <DriveAutoOnSetting connected={isConnected('google_drive')} />
    </div>
  );
};
